import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useUserContext } from 'stores/user';
import { captureSignupFunnelEvent } from 'utils/captureSignupFunnelEvent';
import {
    getSignupFunnelEngagementCount,
    hasSignupFunnelStarted,
    resetSignupFunnelState,
} from 'utils/signupFunnelState';
import { isBookingSignupReferrer, isPartnerSignupFunnelAudience, parseSignupReferrer } from 'utils/signupFunnelVisit';

type SignupVisitSnapshot = {
    url: string;
    referrer: string | undefined;
    mountTime: number;
};

/**
 * Fires signup_page_viewed for unauthenticated partner sign-up visitors once auth
 * bootstrap completes (same audience as PrivateSignUpRoute, excluding logged-in users).
 * Also tracks signup_abandoned on page unload or voluntary client-side navigation away.
 */
export const SignupRouteTracker = () => {
    const location = useLocation();
    const posthog = usePostHog();
    const { user } = useUserContext();
    const hasSentPageView = useRef(false);
    const hasTrackedAbandonment = useRef(false);
    const wasTrackableVisit = useRef(false);
    const visitSnapshotRef = useRef<SignupVisitSnapshot | null>(null);

    const isAuthenticated = user.loaded && Boolean(user.token);
    const isTrackableVisit = isPartnerSignupFunnelAudience({
        pathname: location.pathname,
        search: location.search,
        userLoaded: user.loaded,
        isAuthenticated,
    });
    const parsedReferrer = parseSignupReferrer(location.search);
    const pageUrl = location.pathname + location.search;

    if (isTrackableVisit) {
        const previous = visitSnapshotRef.current;
        visitSnapshotRef.current = {
            url: pageUrl,
            referrer: parsedReferrer,
            mountTime: previous?.mountTime ?? Date.now(),
        };
    }

    const trackAbandonmentRef = useRef<(snapshot: SignupVisitSnapshot) => void>(() => {});
    trackAbandonmentRef.current = (snapshot: SignupVisitSnapshot) => {
        if (hasTrackedAbandonment.current || hasSignupFunnelStarted() || isBookingSignupReferrer(snapshot.referrer)) {
            return;
        }
        hasTrackedAbandonment.current = true;
        captureSignupFunnelEvent(
            posthog,
            'signup_abandoned',
            {
                referrer: snapshot.referrer,
                timeOnPageMs: Date.now() - snapshot.mountTime,
                engaged: getSignupFunnelEngagementCount() > 0,
            },
            { url: snapshot.url }
        );
    };

    useEffect(() => {
        if (wasTrackableVisit.current && !isTrackableVisit) {
            const snapshot = visitSnapshotRef.current;
            if (snapshot) {
                trackAbandonmentRef.current(snapshot);
            }
        }

        if (isTrackableVisit) {
            if (!wasTrackableVisit.current) {
                hasTrackedAbandonment.current = false;
            }
        } else {
            hasSentPageView.current = false;
            visitSnapshotRef.current = null;
            resetSignupFunnelState();
        }

        wasTrackableVisit.current = isTrackableVisit;
    }, [isTrackableVisit]);

    useEffect(() => {
        if (!isTrackableVisit || hasSentPageView.current) {
            return;
        }

        hasSentPageView.current = true;
        captureSignupFunnelEvent(posthog, 'signup_page_viewed', { referrer: parsedReferrer }, { url: pageUrl });
    }, [isTrackableVisit, parsedReferrer, pageUrl, posthog]);

    useEffect(() => {
        if (!isTrackableVisit) {
            return;
        }

        const handlePageHide = (event: PageTransitionEvent) => {
            if (event.persisted) {
                return;
            }
            const snapshot = visitSnapshotRef.current;
            if (snapshot) {
                trackAbandonmentRef.current(snapshot);
            }
        };

        window.addEventListener('pagehide', handlePageHide);

        return () => {
            window.removeEventListener('pagehide', handlePageHide);
        };
    }, [isTrackableVisit]);

    return null;
};
